import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BookOpen,
  ArrowRight,
  Home,
  ChevronDown,
  ChevronUp,
  Sparkles,
  ListChecks,
  RotateCcw,
} from "lucide-react";
import api from "@/lib/api";
import Quiz from "@/components/Quiz";
import { isFullyArabic } from "@/lib/script";

import ConfettiBurst from "@/components/ConfettiBurst";
import { t } from "@/i18n";

/* Mêmes bornes que Quiz.jsx : le bilan occupe exactement la place libre
   entre le header et la barre d'onglets, jamais plus. */
const NAV_TOP = "4rem";
const NAV_BOTTOM = "calc(3.4rem + env(safe-area-inset-bottom))";

/* Normalise les questions du backend au format attendu par Quiz.jsx —
   même forme que buildVocabQuestions, pour que l'affichage riche
   (dir="rtl", bouton de prononciation, explication) soit identique
   partout. L'ordre des options n'est jamais modifié : l'index `answer`
   venant du backend reste valide. */
function buildCourseQuestions(raw) {
  return (raw || []).map((q, i) => {
    const opts = q.options || [];
    return {
      ...q,
      arabicPrompt: q.arabicPrompt ?? isFullyArabic(q.prompt),
      arabicOptions:
        q.arabicOptions ??
        (opts.length > 0 && opts.some((o) => isFullyArabic(o))),
      // Utiliser la vraie clé envoyée par le backend (q.key), jamais q.id
      // qui n'existe pas dans ce payload — sinon on perd le lien vers
      // dblesson:/qpool: et tout finit dans "course#qN".
      key: q.key || `course#q${i}`,
      skill: q.skill || "grammar",
      ref: q.ref || null,
    };
  });
}
export default function CourseQuestions() {
  const { i18n } = useTranslation();
  const nav = useNavigate();
  const lang = i18n.language?.startsWith("en") ? "en" : "fr";

  const [step, setStep] = useState("select"); // select | play | recap
  const [groups, setGroups] = useState([]);
  const [openGroup, setOpenGroup] = useState(null);
  const [selected, setSelected] = useState({});
  const [count, setCount] = useState(15);
  const [questions, setQuestions] = useState([]);
  const [result, setResult] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    api
      .get("/practice/course-questions/lessons")
      .then((r) => {
        setGroups(r.data.groups || []);
        if ((r.data.groups || [])[0]) setOpenGroup(r.data.groups[0].id);
      })
      .catch(() => setNotice(t("coursequestions.notice_lecons_non_chargees")));
  }, []);

  const toggleSelect = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleGroup = (groupId) => {
    const g = groups.find((x) => x.id === groupId);
    if (!g) return;
    const allOn = g.lessons.every((l) => selected[l.id]);
    const upd = { ...selected };
    g.lessons.forEach((l) => {
      upd[l.id] = !allOn;
    });
    setSelected(upd);
  };

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  const start = async () => {
    if (selectedIds.length === 0) return;
    setNotice("");
    try {
      const { data } = await api.post("/practice/course-questions/session", {
        lesson_ids: selectedIds,
        count,
        lang,
      });
      const qs = buildCourseQuestions(data.questions);
      if (qs.length === 0) {
        setNotice(t("coursequestions.notice_aucune_question"));
        return;
      }
      setQuestions(qs);
      setResult(null);
      setRetryKey((k) => k + 1);
      setStep("play");
      window.scrollTo(0, 0);
    } catch {
      setNotice(t("coursequestions.notice_session_non_demarree"));
    }
  };

  const restart = () => {
    setResult(null);
    setRetryKey((k) => k + 1);
    setStep("play");
    window.scrollTo(0, 0);
  };

  const backToSelect = () => {
    setResult(null);
    setStep("select");
    window.scrollTo(0, 0);
  };

  // ── Session ──
  if (step === "play") {
    return (
      <Quiz
        key={retryKey}
        questions={questions}
        onDone={(r) => {
          setResult(r);
          setStep("recap");
          window.scrollTo(0, 0);
        }}
        rewardMode="normal"
        rewardAmount={1}
        showCelebration={false}
        title={t("coursequestions.questions_de_cours")}
        onExit={backToSelect}
      />
    );
  }

  // ── Bilan ──
  if (step === "recap" && result) {
    const total = result.total || questions.length;
    const correct = result.correct || 0;
    const pct = Math.round((100 * correct) / Math.max(1, total));
    const earned = result.earned ?? 0;
    const passed = pct >= 70;

    return (
      <div
        className="fixed inset-x-0 z-30 bg-background flex flex-col md:static md:z-auto md:bg-transparent"
        style={{ top: NAV_TOP, bottom: NAV_BOTTOM }}
        data-testid="course-questions-recap"
      >
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
          <div className="relative overflow-hidden mx-auto w-full max-w-md rounded-3xl border bg-card p-5 text-center">
            {earned > 0 && <ConfettiBurst />}
            <Sparkles className="mx-auto mb-2 w-11 h-11 text-ocre" />
            <h2 className="font-head text-xl font-bold mb-1">
              {t("coursequestions.bilan_questions_de_cours")}
            </h2>
            <p className="text-xs text-muted-foreground mb-4">
              {selectedIds.length} {t("coursequestions.lecon")}
              {selectedIds.length > 1 ? "s" : ""} {t("coursequestions.revisee")}
              {selectedIds.length > 1 ? "s" : ""}
            </p>

            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="rounded-2xl border p-3">
                <div className="font-head text-2xl font-extrabold leading-none">
                  {pct}%
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  {t("coursequestions.score")}
                </div>
              </div>
              <div className="rounded-2xl border p-3">
                <div className="font-head text-2xl font-extrabold leading-none">
                  {correct}/{total}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  {t("coursequestions.bonnes_reponses")}
                </div>
              </div>
            </div>

            <div className="h-2.5 rounded-full bg-secondary overflow-hidden mb-3">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${pct}%`,
                  backgroundColor: passed
                    ? "#16A34A"
                    : "hsl(var(--destructive))",
                }}
              />
            </div>

            <div className="rounded-2xl bg-amber-500/10 py-3 mb-4">
              <div className="font-head text-3xl font-extrabold text-amber-500 leading-none">
                +{earned} 🪙
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">
                {earned > 0
                  ? t("coursequestions.gagnes_sur_cette_session")
                  : t("quiz.aucun_dinar_cette_fois_retente")}
              </div>
            </div>

            <button
              onClick={restart}
              className="w-full py-3 rounded-2xl bg-primary text-primary-foreground font-bold flex items-center justify-center gap-2 mb-2"
            >
              <RotateCcw size={16} />{" "}
              {t("coursequestions.refaire_cette_session")}
            </button>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <button
                onClick={backToSelect}
                className="py-2.5 rounded-2xl border font-semibold text-sm flex items-center justify-center gap-1.5"
              >
                <ListChecks size={14} /> {t("coursequestions.selection")}
              </button>
              <button
                onClick={() => nav("/dashboard")}
                className="py-2.5 rounded-2xl border font-semibold text-sm flex items-center justify-center gap-1.5"
              >
                <Home size={14} /> {t("coursequestions.accueil")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Sélection ──
  return (
    <div className="max-w-3xl mx-auto pb-40">
      <div className="mb-5">
        <h1 className="font-head text-2xl sm:text-3xl font-extrabold mb-1 flex items-center gap-2">
          <BookOpen size={24} className="text-primary" />{" "}
          {t("coursequestions.questions_de_cours")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t("coursequestions.choisis_les_lecons_a_reviser_les")}
        </p>
      </div>

      {notice && (
        <div className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-3 mb-4 text-sm text-amber-800 dark:text-amber-300">
          {notice}
        </div>
      )}

      <div className="rounded-2xl border bg-card p-4 mb-4 flex items-center justify-between gap-3">
        <span className="text-sm font-medium">
          {t("coursequestions.nombre_de_questions")}
        </span>
        <input
          type="number"
          min={5}
          max={50}
          value={count}
          onChange={(e) =>
            setCount(
              Math.min(50, Math.max(5, parseInt(e.target.value, 10) || 15)),
            )
          }
          className="px-3 py-2 rounded-xl border bg-background w-20 text-center font-head font-bold"
        />
      </div>

      <div className="space-y-3">
        {groups.map((g) => (
          <div
            key={g.id}
            className="rounded-2xl border bg-card overflow-hidden"
          >
            <div className="flex items-center gap-2 p-3 sm:p-4">
              <button
                onClick={() => setOpenGroup((o) => (o === g.id ? null : g.id))}
                className="flex items-center gap-2 flex-1 min-w-0 text-left"
              >
                {openGroup === g.id ? (
                  <ChevronUp size={18} className="shrink-0" />
                ) : (
                  <ChevronDown size={18} className="shrink-0" />
                )}
                <span className="font-head font-bold truncate">{g.title}</span>
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-secondary shrink-0">
                  {g.level || g.skill}
                </span>
              </button>
              <button
                onClick={() => toggleGroup(g.id)}
                className="text-xs text-primary hover:underline shrink-0"
              >
                {t("coursequestions.tout_cocher")}
              </button>
            </div>
            {openGroup === g.id && (
              <div className="px-3 sm:px-4 pb-3 space-y-1">
                {g.lessons.map((l) => (
                  <label
                    key={l.id}
                    className="flex items-center gap-3 py-2 border-t cursor-pointer hover:bg-secondary/40 rounded px-2 transition"
                  >
                    <input
                      type="checkbox"
                      checked={!!selected[l.id]}
                      onChange={() => toggleSelect(l.id)}
                      className="w-4 h-4 accent-primary shrink-0"
                    />
                    <span className="flex-1 min-w-0 text-sm truncate">
                      {l.title}
                    </span>
                    <span className="text-[11px] text-muted-foreground shrink-0">
                      {l.question_count} q · {l.minutes} min
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Bouton de validation toujours visible */}
      <div
        className="fixed bottom-0 inset-x-0 z-30 border-t bg-background/95 backdrop-blur-xl px-4 pt-3"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 4.5rem)" }}
      >
        <div className="max-w-3xl mx-auto">
          <button
            onClick={start}
            disabled={selectedIds.length === 0}
            className="w-full py-4 rounded-2xl bg-primary text-primary-foreground font-bold disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {selectedIds.length === 0
              ? t("coursequestions.choisis_une_lecon")
              : t("coursequestions.lancer_avec_n_lecons", {
                  count: selectedIds.length,
                  s: selectedIds.length > 1 ? "s" : "",
                })}
            <ArrowRight size={17} />
          </button>
        </div>
      </div>
    </div>
  );
}
